import React, { useState, useEffect } from 'react';
import {
    initializeDraft,
    getDraftStatus,
    getNextPack,
    selectFromPack,
    autodraftSecureTeam
} from '../../DatabaseAPI';
import DynamicPackDisplay from './DynamicPackDisplay';
import PackSelectionInterface from './PackSelectionInterface';
import DraftProgressTracker from './DraftProgressTracker';
import PlayerPoolModal from './PlayerPoolModal';
import GearPoolModal from './GearPoolModal';
import TutorialPopup from '../tutorial/TutorialPopup';
import { normalizeComposition, getPositionCount } from '../utils/draftComposition';
import { getGearImageUrl, handleGearImageError } from '../../utils/GearImageHelpers';
import { FALLBACK_PLAYER_IMAGE } from '../../api/shopApi';
import { resolveMediaUrl } from '../../api/transport';
import './SequentialPackSystem.css';

// Mobile detection hook
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return isMobile;
};

// Helper functions for data extraction
const extractTeamFromMatchup = (matchupDisplay) => {
    if (!matchupDisplay) return '';
    // Example: "TB vs ATL" -> return "TB"
    const parts = matchupDisplay.split(' ');
    return parts[0] || '';
};

const extractOpponentFromMatchup = (matchupDisplay) => {
    if (!matchupDisplay) return '';
    // Example: "TB vs ATL" -> return "ATL"
    const parts = matchupDisplay.split(' vs ');
    return parts[1] || matchupDisplay;
};

const getCardRarityColor = (rarity) => {
    switch (rarity) {
        case 'LEGENDARY': return '#FFD700'; // Gold
        case 'RARE': return '#9A4FFF'; // Purple
        case 'SILVER': return '#C0C0C0'; // Silver
        case 'BRONZE': return '#CD7F32'; // Bronze
        case 'COMMON': return '#8B8B8B'; // Gray
        default: return '#3498db'; // Blue
    }
};

// Position-based gear color constants
const GEAR_POSITION_COLORS = {
    QB: '#f1c40f',        // Yellow
    RB: '#3498db',        // Blue
    WR: '#e74c3c',        // Red
    QB_RB: '#27ae60',     // Green (hybrid QB/RB)
    RB_WR: '#9b59b6',     // Purple (hybrid RB/WR)
    NONE: '#ffffff'       // White (no match)
};

/**
 * Analyzes gear description to determine which position(s) it affects
 * @param {string} description - The gear's player_description or description
 * @returns {Object} - { isQB, isRB, isWR } boolean flags
 */
const analyzeGearPosition = (description) => {
    if (!description) return { isQB: false, isRB: false, isWR: false };
    
    const lowerDesc = description.toLowerCase();
    
    // QB keywords - anything with pass, completion, interception
    const isQB = /pass|completion|interception/i.test(lowerDesc);
    
    // RB keywords - anything with rush, carr (matches carry/carries)
    const isRB = /rush|carr/i.test(lowerDesc);
    
    // WR keywords - anything with reception, receiving, target, rec (short for reception)
    const isWR = /reception|receiving|target|\brec\b/i.test(lowerDesc);
    
    return { isQB, isRB, isWR };
};

/**
 * Gets the background color for a gear card based on position analysis
 * @param {Object} gear - The gear item object
 * @returns {string} - CSS color value
 */
const getGearPositionColor = (gear) => {
    const description = gear.player_description || gear.description || '';
    const { isQB, isRB, isWR } = analyzeGearPosition(description);
    
    // Hybrid cards (both positions match)
    if ((isQB && isRB) || (isQB && isWR)) return GEAR_POSITION_COLORS.QB_RB; // Green for QB/RB
    if (isRB && isWR) return GEAR_POSITION_COLORS.RB_WR; // Purple for RB/WR
    
    // Single position cards
    if (isQB) return GEAR_POSITION_COLORS.QB; // Yellow
    if (isRB) return GEAR_POSITION_COLORS.RB; // Blue
    if (isWR) return GEAR_POSITION_COLORS.WR; // Red
    
    // No match - keep white/default
    return GEAR_POSITION_COLORS.NONE;
};

// Helper function to create unique card identifiers for selection
const getCardUniqueId = (card, index) => {
    // For dynamic packs: use slot_number if available
    if (card.slot_number !== undefined) {
        return `slot_${card.slot_number}`;
    }
    // Check if card has raw slot_number data (before legacy transformation)
    if (card.content && card.content.slot_number !== undefined) {
        return `slot_${card.content.slot_number}`;
    }
    // For legacy packs: use card.type + index to ensure uniqueness
    if (card.type) {
        return `${card.type}_${index}`;
    }
    // Fallback: use array index
    return `card_${index}`;
};

// Simple Card Info Popup Component (similar to PlayerEquipmentPanel style)
const CardInfoPopup = ({ card, isOpen, onClose, position, isMobile }) => {
    if (!isOpen || !card) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                padding: '20px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: '#f8f9fa',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    padding: '20px',
                    maxWidth: isMobile ? '280px' : '320px',
                    width: '100%',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                    position: 'relative'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'none',
                        border: 'none',
                        fontSize: '18px',
                        cursor: 'pointer',
                        color: '#666',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%'
                    }}
                    onMouseOver={e => e.target.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.target.style.backgroundColor = 'transparent'}
                >
                    ×
                </button>

                {/* No debug info in production */}
                
                {/* Card Header */}
                <h4 style={{
                    margin: '0 0 15px 0',
                    color: '#2c3e50',
                    borderBottom: '2px solid #ecf0f1',
                    paddingBottom: '8px',
                    fontSize: '16px'
                }}>
                    {card.content?.name || card.content?.player_name || 'Card Info'}
                </h4>

                {/* Card Details */}
                <div>
                    {/* For Gear Cards - CHECK CONTENT_TYPE! */}
                    {card.content_type === 'gear' && (
                        <div>
                            {/* Gear Slot */}
                            {card.content && card.content.slot && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px',
                                    margin: '8px 0',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #9b59b6',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 6px rgba(155,89,182,0.2)'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            color: '#9b59b6'
                                        }}>
                                            Equipment Slot: {card.content.slot}
                                        </div>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#7f8c8d',
                                            marginTop: '2px'
                                        }}>
                                            Can be equipped to {card.content.slot} slot
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Description section removed as requested */}

                            {/* Item effect text - Enhanced Prominence.
                                `player_description` (dynamic path) and `description`
                                (legacy serializer path) are the same gear effect copy. */}
                            {card.content && (card.content.player_description || card.content.description) && (
                                <div style={{
                                    padding: '12px',
                                    margin: '12px 0',
                                    backgroundColor: '#ffffff',
                                    border: '3px solid #3498db',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 8px rgba(52,152,219,0.3)'
                                }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        color: '#3498db',
                                        marginBottom: '8px'
                                    }}>
                                        Item Description
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#2c3e50',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.5'
                                    }}>
                                        {card.content.player_description || card.content.description}
                                    </div>
                                </div>
                            )}

                            {/* Keeping only slot and item description as requested */}
                        </div>
                    )}

                    {/* For Player Cards - CHECK CONTENT_TYPE! */}
                    {card.content_type === 'player' && card.content && (
                        <div>
                            {/* Player Position & Team */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px',
                                margin: '8px 0',
                                backgroundColor: '#ffffff',
                                border: '2px solid #3498db',
                                borderRadius: '6px',
                                boxShadow: '0 2px 6px rgba(52,152,219,0.2)'
                            }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: '#3498db'
                                    }}>
                                        Position: {card.content.position || card.type}
                                    </div>
                                    {card.content.matchup_display && (
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#7f8c8d',
                                            marginTop: '2px'
                                        }}>
                                            <span style={{ color: '#e74c3c', fontWeight: 700 }}>Matchup: </span>
                                            {card.content.matchup_display}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Projected Points */}
                            {card.content.projected_points && (
                                <div style={{
                                    padding: '10px',
                                    margin: '8px 0',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #27ae60',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 6px rgba(39,174,96,0.2)'
                                }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: '#27ae60',
                                        marginBottom: '5px'
                                    }}>
                                        Projected Fantasy Points
                                    </div>
                                    <div style={{
                                        fontSize: '16px',
                                        color: '#2c3e50',
                                        fontWeight: 'bold',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.4'
                                    }}>
                                        {Math.round(card.content.projected_points * 10) / 10} points
                                    </div>
                                </div>
                            )}

                            {/* Position Rank */}
                            {card.content.position_rank && (
                                <div style={{
                                    padding: '10px',
                                    margin: '8px 0',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #f39c12',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 6px rgba(243,156,18,0.2)'
                                }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: '#f39c12',
                                        marginBottom: '5px'
                                    }}>
                                        Position Ranking
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#636e72',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.4'
                                    }}>
                                        #{card.content.position_rank} {card.content.position}
                                    </div>
                                </div>
                            )}

                            {/* Player Description if available */}
                            {card.content.description && (
                                <div style={{
                                    padding: '10px',
                                    margin: '8px 0',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #27ae60',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 6px rgba(39,174,96,0.2)'
                                }}>
                                    <div style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        color: '#27ae60',
                                        marginBottom: '5px'
                                    }}>
                                        Description
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#636e72',
                                        fontStyle: 'italic',
                                        wordBreak: 'break-word',
                                        lineHeight: '1.4'
                                    }}>
                                        {card.content.description}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Rarity Info */}
                    <div style={{
                        textAlign: 'center',
                        marginTop: '15px',
                        padding: '8px',
                        backgroundColor: '#ecf0f1',
                        borderRadius: '6px'
                    }}>
                        <div style={{
                            fontSize: '12px',
                            color: '#7f8c8d',
                            marginBottom: '4px'
                        }}>
                            Rarity
                        </div>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: getCardRarityColor(card.content?.rarity_tier || card.content?.rarity)
                        }}>
                            {card.content?.rarity_tier || card.content?.rarity || 'COMMON'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * SequentialPackSystem - New Phase 4 pack system implementation
 * - Sequential pack order: 1 Legendary → 4 Rare → 10 Value
 * - Anti-cheat verification with backend
 * - Single pack display with 5 cards (1QB + 1RB + 1WR + 2 gear)
 * - Real opponent data display
 */
function SequentialPackSystem({ user, teamId, leagueId, setPage, onComplete }) {
    const [draftState, setDraftState] = useState(null);
    const [currentPack, setCurrentPack] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOpeningPack, setIsOpeningPack] = useState(false);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [modalCard, setModalCard] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [enlargedItem, setEnlargedItem] = useState(null);
    const [showEnlargedPopup, setShowEnlargedPopup] = useState(false);
    const [showWelcomeSplash, setShowWelcomeSplash] = useState(true);
    const [showPlayerPoolModal, setShowPlayerPoolModal] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [showGearPoolModal, setShowGearPoolModal] = useState(false);
    const isMobile = useIsMobile();
    
    // Tutorial state for first pack
    const [showFirstPackTutorial, setShowFirstPackTutorial] = useState(false);
    const [firstPackTutorialData] = useState({
        page_identifier: 'first_pack_draft',
        title: 'Your First Pack',
        content_html: '<p>Select the pack to see your options!</p>'
    });
    
    // Enhanced state for Phase 3 dynamic pack system
    const [packSelectionMode, setPackSelectionMode] = useState('SEQUENTIAL');
    const [availablePacks, setAvailablePacks] = useState([]);
    const [currentPackConfig, setCurrentPackConfig] = useState(null);
    const [showPackSelection, setShowPackSelection] = useState(false);
    const [catalogInfo, setCatalogInfo] = useState(null);
    
    // Use effective teamId with localStorage fallback
    const [effectiveTeamId, setEffectiveTeamId] = useState(null);
    
    // Handle teamId with localStorage fallback
    useEffect(() => {
        let resolvedTeamId = teamId || user?.id;
        
        // If still no teamId, try localStorage
        if (!resolvedTeamId) {
            const storedTeamId = localStorage.getItem('draftTeamId');
            if (storedTeamId) {
                console.log('SequentialPackSystem: Recovered teamId from localStorage:', storedTeamId);
                resolvedTeamId = storedTeamId;
            }
        }
        
        // Store valid teamId in localStorage for future use
        if (resolvedTeamId) {
            localStorage.setItem('draftTeamId', resolvedTeamId);
            setEffectiveTeamId(resolvedTeamId);
        } else {
            console.error('SequentialPackSystem: No teamId available from props, user, or localStorage');
            setEffectiveTeamId(null);
        }
    }, [teamId, user?.id]);

    useEffect(() => {
        if (effectiveTeamId) {
            initializeOrLoadDraft();
        }
    }, [effectiveTeamId]);

    const initializeOrLoadDraft = async () => {
        if (!effectiveTeamId) {
            setError('No team ID available. Please return to team selection.');
            setIsLoading(false);
            return;
        }
        
        try {
            setIsLoading(true);
            setError(null);

            // Check if draft exists - enhanced API from Phase 2C
            const status = await getDraftStatus(effectiveTeamId);

            if (!status.has_draft) {
                // Initialize new draft
                console.log('SequentialPackSystem: Initializing new draft');
                await initializeDraft(effectiveTeamId);

                // Get updated status with enhanced information
                const newStatus = await getDraftStatus(effectiveTeamId);
                setDraftState(newStatus.draft_state);
                setCurrentPack(newStatus.current_pack);
                
                // Set enhanced draft information from Phase 2C
                setCatalogInfo(newStatus.catalog_info);
                setPackSelectionMode(newStatus.catalog_info?.pack_selection_mode || 'SEQUENTIAL');
                // getDraftStatus() carries catalog_info on the envelope, so the second
                // argument is redundant here — passed anyway so every call site reads
                // the same way and none can be mistaken for "no catalog needed".
                setShowPackSelection(shouldShowPackSelection(newStatus, newStatus.catalog_info));
            } else {
                // Load existing draft
                console.log('SequentialPackSystem: Loading existing draft');
                setDraftState(status.draft_state);
                setCurrentPack(status.current_pack);
                
                // Set enhanced draft information
                setCatalogInfo(status.catalog_info);
                setPackSelectionMode(status.catalog_info?.pack_selection_mode || 'SEQUENTIAL');
                setShowPackSelection(shouldShowPackSelection(status, status.catalog_info));
                
                // Show first pack tutorial if this is the first pack and not opened yet.
                // Field name is total_packs_completed — TeamDraftStateSerializer.Meta.fields
                // (pack_serializers.py:205). There is no `packs_completed` key on
                // draft_state; reading it made this condition permanently false and
                // silently suppressed the first-pack tutorial.
                if (status.draft_state?.total_packs_completed === 0 && !status.current_pack) {
                    const tutorialShown = localStorage.getItem('tutorial_viewed_first_pack_draft');
                    const tutorialsDisabled = localStorage.getItem('tutorials_disabled');
                    if (!tutorialShown && !tutorialsDisabled) {
                        setShowFirstPackTutorial(true);
                    }
                }
            }
        } catch (error) {
            console.error('SequentialPackSystem: Error initializing draft:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };
    
    /**
     * Decides whether the pack-CHOICE interface should be shown next.
     *
     * The first argument is a FULL response ENVELOPE (not the `draft_state`
     * sub-object) — callers pass `status` / `newStatus` / `selectionResponse`. Its two
     * inputs come from two different places:
     *
     *  · `pack_selection_mode` lives on `catalog_info`, a TOP-LEVEL envelope key that
     *    ONLY the getDraftStatus() response carries. SelectFromPackView returns
     *    `draft_state` and **no `catalog_info` at all**
     *    (pack_views.py:619-631 dynamic / :679-690 legacy).
     *  · `upcoming_packs` is emitted by TeamDraftStateSerializer and therefore always
     *    lives at `<envelope>.draft_state.upcoming_packs` (pack_serializers.py:197).
     *    Reading it off the envelope was always `undefined`.
     *
     * WHY `fallbackCatalogInfo` EXISTS — the select-card regression it prevents:
     * after the first card of a CHOICE/HYBRID draft, `handleCardSelect()` calls this
     * with the select-card response. That response has no `catalog_info`, so
     * `?.pack_selection_mode || 'SEQUENTIAL'` silently downgraded the draft to
     * SEQUENTIAL: `PackSelectionInterface` was hidden and every remaining pack was
     * routed through `getNextPack()`, so the user chose a pack exactly once and never
     * again. Callers therefore pass the catalog they ALREADY hold. The catalog cannot
     * change mid-draft, so the stored value is authoritative for the whole draft —
     * this is a missing-key problem, not a stale-data one, and needs no extra fetch.
     *
     * A genuinely absent catalog on BOTH sides is logged loudly rather than quietly
     * defaulting: `catalog_info` is legitimately `null` when a team has no draft
     * catalog (pack_views.py:997-999), so this cannot throw — but it must never pass
     * unnoticed, because it is indistinguishable from the bug above.
     */
    const shouldShowPackSelection = (responseEnvelope, fallbackCatalogInfo = null) => {
        const catalog = responseEnvelope.catalog_info || fallbackCatalogInfo;

        if (!catalog) {
            console.error(
                '[SequentialPackSystem] CONTRACT GAP: shouldShowPackSelection() got no catalog_info ' +
                'on the response AND no stored catalogInfo to fall back on — defaulting to SEQUENTIAL. ' +
                'If this draft is CHOICE/HYBRID, the pack-choice interface is being wrongly hidden.',
                {
                    envelopeKeys: Object.keys(responseEnvelope),
                    draftStateKeys: responseEnvelope.draft_state
                        ? Object.keys(responseEnvelope.draft_state)
                        : null
                }
            );
        }

        // Show pack selection interface ONLY for explicit choice modes.
        // DEFAULT to SEQUENTIAL only when the mode is genuinely absent from the
        // catalog itself — never merely because THIS response omitted catalog_info.
        const selectionMode = catalog?.pack_selection_mode || 'SEQUENTIAL';
        const upcomingPacks = responseEnvelope.draft_state?.upcoming_packs;

        return Boolean(
            (selectionMode === 'CHOICE' || selectionMode === 'HYBRID') &&
            upcomingPacks && upcomingPacks.length > 1
        );
    };

    const handleOpenPack = async () => {
        if (!draftState || draftState.is_draft_complete || !effectiveTeamId) {
            return;
        }

        try {
            setIsOpeningPack(true);
            setError(null);

            // Get next pack from backend - enhanced API
            const packResponse = await getNextPack(effectiveTeamId);

            if (packResponse.success) {
                // Backend now returns consistent format for both legacy and dynamic
                setCurrentPack(packResponse.pack);
                setCurrentPackConfig(packResponse.pack?.pack_config);
                setDraftState(packResponse.draft_state);
                
                // Update catalog info if provided
                if (packResponse.catalog_info) {
                    setCatalogInfo(packResponse.catalog_info);
                }
            } else {
                throw new Error(packResponse.message || 'Failed to open pack');
            }
        } catch (error) {
            console.error('SequentialPackSystem: Error opening pack:', error);

            // Check if error indicates draft completion
            if (error.message && error.message.includes('Draft is already complete')) {
                // Refresh draft state to show completion
                try {
                    const status = await getDraftStatus(effectiveTeamId);
                    if (status.draft_state) {
                        setDraftState(status.draft_state);
                        setCatalogInfo(status.catalog_info);
                    }
                } catch (statusError) {
                    console.error('Error getting draft status:', statusError);
                }
                setError(null); // Clear error since this is expected behavior
            } else {
                setError(error.message);
            }
        } finally {
            setIsOpeningPack(false);
        }
    };

    const handleCardSelect = async (card, cardId) => {
        if (!currentPack || selectedCardId) {
            return;
        }

        // Prevent selection of hidden cards
        if (card.hidden) {
            console.log('Card not selectable: hidden slot');
            return;
        }

        try {
            setSelectedCardId(cardId);
            setError(null);

            // Determine selection data based on card type and available information
            let selectionData;
            
            // Check if this is a dynamic pack with slot numbers
            const hasSlotNumber = card.slot_number !== undefined || (card.content && card.content.slot_number !== undefined);
            
            if (hasSlotNumber) {
                // Dynamic pack: use slot-based selection
                const slotNumber = card.slot_number || card.content.slot_number;
                selectionData = {
                    card_slot_number: slotNumber,
                    card_type: card.type  // For backend compatibility
                };
                console.log('🔄 DYNAMIC SELECTION:', selectionData);
            } else {
                // Legacy pack: use type-based selection
                selectionData = card.type;
                console.log('🔄 LEGACY SELECTION:', selectionData);
            }

            // Call selectFromPack with enhanced data
            const selectionResponse = await selectFromPack(
                effectiveTeamId,
                currentPack.pack_number,
                selectionData
            );

            if (selectionResponse.success) {
                // Update draft state
                setDraftState(selectionResponse.draft_state);

                // SelectFromPackView does not return catalog_info, so in practice this
                // resolves to the catalog stored at draft init. Held in a local because
                // `catalogInfo` state would still be the PREVIOUS value in this tick.
                const effectiveCatalogInfo = selectionResponse.catalog_info || catalogInfo;

                // Update catalog info if provided
                if (selectionResponse.catalog_info) {
                    setCatalogInfo(selectionResponse.catalog_info);
                }

                // Check if draft is complete
                if (selectionResponse.draft_complete || selectionResponse.draft_state?.is_draft_complete) {
                    // Draft complete - keep current pack visible and show completion
                } else {
                    // Check if we need to show pack selection interface.
                    // The stored catalog MUST be passed: this response carries no
                    // catalog_info, and without it a CHOICE/HYBRID draft reads as
                    // SEQUENTIAL from the second pack onward.
                    if (shouldShowPackSelection(selectionResponse, effectiveCatalogInfo)) {
                        setShowPackSelection(true);
                        setCurrentPack(null);
                        setSelectedCardId(null);
                    } else {
                        // Reset for next pack only if draft is not complete
                        setTimeout(() => {
                            setCurrentPack(null);
                            setSelectedCardId(null);
                        }, 1500);
                    }
                }
            } else {
                throw new Error(selectionResponse.message || 'Failed to select card');
            }
        } catch (error) {
            console.error('SequentialPackSystem: Error selecting card:', {
                error,
                message: error.message,
                teamId: effectiveTeamId,
                packNumber: currentPack?.pack_number,
                card
            });
            setError(
                `We could not save your card selection for pack ${currentPack?.pack_number ?? 'unknown'}. ` +
                `${error.message || 'Please retry.'}`
            );
            setSelectedCardId(null);
        }
    };
    
    const handlePackSelected = async (pack, packContents) => {
        // Handle pack selection from choice interface
        setCurrentPack(packContents.pack);
        setCurrentPackConfig(packContents.pack?.pack_config);
        setShowPackSelection(false);
        setSelectedCardId(null);
    };

    const handleAutodraft = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const autodraftResponse = await autodraftSecureTeam(effectiveTeamId);

            if (autodraftResponse.success) {
                setPage(1); // Back to team page (original behavior)
            } else {
                throw new Error(autodraftResponse.message || 'Autodraft failed');
            }
        } catch (error) {
            console.error('SequentialPackSystem: Error autodrafting:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAutopick = async () => {
        if (!currentPack || !currentPack.cards || selectedCardId) {
            return;
        }

        // Get only available cards for selection
        const availableCards = currentPack.cards.filter(card =>
            currentPack.available_selections?.includes(card.type)
        );

        if (availableCards.length === 0) {
            console.error('No available cards for autopick');
            return;
        }

        // Select a random card from available cards only
        const randomIndex = Math.floor(Math.random() * availableCards.length);
        const randomCard = availableCards[randomIndex];
        const randomCardId = getCardUniqueId(randomCard, currentPack.cards.indexOf(randomCard));

        await handleCardSelect(randomCard, randomCardId);
    };

    // Handle info button click
    const handleCardInfo = (card) => {
        setModalCard(card);
        setIsModalOpen(true);
    };

    // Close modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setModalCard(null);
    };

    const getPackTypeLabel = (packType, packNumber) => {
        // The count shown is packs left in the WHOLE draft, not packs left of
        // this type. See getTotalPacksRemaining(): the backend exposes no
        // per-type breakdown, and inventing one would be a guess. The wording
        // says "left" (not "N Legendary remaining") so the number can't be
        // misread as a per-type figure.
        const remaining = getTotalPacksRemaining();
        const remainingLabel = remaining === null ? '—' : remaining;
        switch (packType) {
            case 'LEGENDARY':
                return `✨ Legendary Pack - ${remainingLabel} packs left`;
            case 'RARE':
                return `💎 Rare Pack - ${remainingLabel} packs left`;
            // Legacy catalogs emit VALUE, dynamic catalogs emit COMMON for the
            // same tier.
            case 'VALUE':
            case 'COMMON':
                return `📦 Value Pack - ${remainingLabel} packs left`;
            default:
                return `Pack ${packNumber}`;
        }
    };

    const getCardImgSrc = (card) => {
        // CHECK CONTENT_TYPE NOT TYPE! This is the key fix
        if (card.content_type === 'gear') {
            console.log('🎯 Gear content detected, using getGearImageUrl');
            return getGearImageUrl(card.content);
        }
        
        // Handle player cards - use GSIS ID from backend data
        if (card.content_type === 'player') {
            // Prefer backend-resolved presentation URL (equipped variant or default art gateway).
            // resolveMediaUrl converts the relative /api/... path to an absolute backend URL
            // in dev (where the React dev server and Django run on different ports).
            if (card.content?.presentation?.image_url) {
                return resolveMediaUrl(card.content.presentation.image_url);
            }
            const gsisId = card.content?.gsis_id || card.content?.source_player_id;
            const playerName = card.content?.player_name || 'Unknown';

            console.log('🏈 Player content detected:', {
                playerName,
                cardType: card.type,
                contentType: card.content_type,
                gsisId,
            });

            if (!gsisId) return FALLBACK_PLAYER_IMAGE;
            return `/art/player_images_gsis_cartoon/${gsisId}.png`;
        }
        
        // Fallback
        return '/art/mystery-item2.png';
    };

    // Packs left in the draft, INCLUDING the current unopened pack.
    //
    // WHY NOT PER-TYPE: the backend has no per-type breakdown. `packs_remaining`
    // is a plain INTEGER on the draft-status envelope (pack_views.py:1021, from
    // Team.packs_remaining) and does not appear on `draft_state` at all — there
    // is no {LEGENDARY, RARE, VALUE, COMMON, total} object anywhere, and
    // `catalog_info` carries only `total_packs`, no per-tier counts. A per-type
    // figure would therefore have to be invented, so it isn't shown.
    //
    // Both inputs come from the backend (never hardcoded): pack counts vary by
    // league template, so a literal total would be wrong for most catalogs.
    // Returns null (logged) rather than a believable 0 when either is missing,
    // so callers render a visibly-degraded '—'. A genuine 0 still renders 0.
    const getTotalPacksRemaining = () => {
        const totalPacks = catalogInfo?.total_packs;
        const completed = draftState?.total_packs_completed;

        if (typeof totalPacks !== 'number' || typeof completed !== 'number') {
            console.error(
                '[SequentialPackSystem] CONTRACT VIOLATION: cannot compute packs remaining — ' +
                'need catalog_info.total_packs and draft_state.total_packs_completed, both numbers.',
                {
                    total_packs: totalPacks,
                    total_packs_typeof: typeof totalPacks,
                    total_packs_completed: completed,
                    total_packs_completed_typeof: typeof completed,
                    currentPackNumber: currentPack?.pack_number ?? null,
                    catalogInfoKeys: catalogInfo ? Object.keys(catalogInfo) : null,
                    draftStateKeys: draftState ? Object.keys(draftState) : null
                }
            );
            return null;
        }

        return Math.max(0, totalPacks - completed);
    };

    // Helper to read total_packs_completed. The backend always emits this on
    // draft_state — the field is named `total_packs_completed`, per
    // TeamDraftStateSerializer.Meta.fields (pack_serializers.py:204-209). There is
    // NO `packs_completed` key; reading that name made this guard fire on every
    // healthy draft and render "—/19".
    // `|| 0` here would turn a missing field into a plausible "0/19" that reads as
    // a legitimate fresh draft. Returns null (after a loud console.error) so
    // callers render a visibly-degraded value instead. A real 0 still renders 0.
    const getPacksCompleted = (draftState) => {
        const packsCompleted = draftState?.total_packs_completed;
        if (typeof packsCompleted !== 'number') {
            console.error(
                '[SequentialPackSystem] CONTRACT VIOLATION: draft_state.total_packs_completed is missing or not a number. ' +
                'The backend guarantees this field on every payload containing draft_state.',
                {
                    total_packs_completed: packsCompleted,
                    total_packs_completed_typeof: typeof packsCompleted,
                    currentPackNumber: currentPack?.pack_number ?? null,
                    draftStateKeys: draftState ? Object.keys(draftState) : null
                }
            );
            return null;
        }
        return packsCompleted;
    };

    const getCardRarityBorder = (rarity) => {
        switch (rarity) {
            case 'LEGENDARY': return '3px solid #FFD700'; // Gold border
            case 'RARE': return '3px solid #9A4FFF'; // Purple border
            case 'SILVER': return '3px solid #C0C0C0'; // Silver border
            case 'BRONZE': return '3px solid #CD7F32'; // Bronze border
            case 'COMMON': return '3px solid #8B8B8B'; // Gray border
            default: return '2px solid #ddd'; // Default border
        }
    };

    if (isLoading) {
        return (
            <div className="sequential-pack-system" style={{
                minHeight: '100vh',
                backgroundImage: 'url(/art/stadium.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}>
                    <div style={{ fontSize: '24px', marginBottom: '20px' }}>🎯 Initializing Draft...</div>
                    <div style={{ fontSize: '16px', color: '#666' }}>
                        Setting up your sequential pack experience
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="sequential-pack-system" style={{
                minHeight: '100vh',
                backgroundImage: 'url(/art/stadium.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                padding: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}>
                    <div style={{ fontSize: '24px', marginBottom: '20px', color: '#e74c3c' }}>
                        ❌ Draft Error
                    </div>
                    <div style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
                        {error}
                    </div>
                    <button
                        onClick={initializeOrLoadDraft}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            marginRight: '10px'
                        }}
                    >
                        Retry
                    </button>
                    <button
                        onClick={() => setPage(1)}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#95a5a6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        );
    }

    const renderDraftInterface = () => {
        if (draftState?.is_draft_complete) {
            // Set flag to show gear tutorial after draft completion
            sessionStorage.setItem('showGearTutorial', 'true');
            // Automatically redirect to GearPage (page 8) when draft completes
            setPage(8);
            return null;
        }
        
        // CRITICAL FIX: Only use Phase 3 components for dynamic catalogs with choice modes
        const useDynamicInterface = catalogInfo?.use_dynamic_packs === true;
        const isChoiceMode = catalogInfo?.pack_selection_mode === 'CHOICE' ||
                           catalogInfo?.pack_selection_mode === 'HYBRID';
        
        console.log('🔍 RENDER DECISION:', {
            useDynamicInterface,
            isChoiceMode,
            catalogInfo,
            showPackSelection
        });
        
        if (useDynamicInterface && isChoiceMode && showPackSelection) {
            console.log('📦 Using Phase 3 PackSelectionInterface for choice mode');
            return (
                <PackSelectionInterface
                    teamId={effectiveTeamId}
                    draftState={draftState}
                    catalog={catalogInfo}
                    onPackSelected={handlePackSelected}
                />
            );
        }
        
        if (currentPack) {
            // Dynamic packs use the NextPackView nested-card contract regardless
            // of their count. A five-card dynamic Season pack is still dynamic;
            // routing it through the legacy display bypassed its selectable gate
            // and made the rendering path depend on an unrelated card count.
            if (useDynamicInterface) {
                console.log('📦 Using DynamicPackDisplay for dynamic pack', {
                    packNumber: currentPack.pack_number,
                    cardCount: currentPack.card_count
                });
                return (
                    <div className="current-pack-interface">
                        <DynamicPackDisplay
                            pack={currentPack}
                            onCardSelect={handleCardSelect}
                            draftState={draftState}
                            isMobile={isMobile}
                        />
                    </div>
                );
            } else {
                console.log('📦 Using legacy pack display for sequential mode');
                return renderLegacyPackDisplay();
            }
        }
        
        // Show pack to open
        return renderPackOpener();
    };
    
    const renderLegacyPackDisplay = () => {
        // COMPLETE ORIGINAL legacy display logic (FULLY RESTORED)
        return (
            <div style={{
                textAlign: 'center',
                maxWidth: '900px',
                width: '100%'
            }}>
                <h2 style={{ margin: '0 0 20px 0', color: '#ffffff' }}>
                    {getPackTypeLabel(currentPack.pack_type, currentPack.pack_number)}
                </h2>
                <p style={{
                 color: '#ffffff',
                 marginTop: '7px',
     marginBottom: '30px'
                   }}>
     Select ONE player or gear card from the pack below:
                </p>

                {/* COMPLETE ORIGINAL 5-card display with ALL overlays */}
                <div style={{
                    display: 'flex',
                    gap: '15px',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                }}>
                {currentPack.cards.filter(card => !card.hidden).map((card, displayIndex) => {
                    // Get original card index from full cards array for proper selection
                    const originalIndex = currentPack.cards.indexOf(card);
                    const cardId = getCardUniqueId(card, originalIndex);
                    const isDisabled = selectedCardId;
                    const imgSrc = getCardImgSrc(card);
                    const isSelected = selectedCardId === cardId;

                    return (
                        <div
                            key={cardId}
                            onClick={() => !isDisabled && handleCardSelect(card, cardId)}
                            style={{
                                width: '159px',
                                height: '219px',
                                position: 'relative',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                border: isSelected ? '3px solid #27ae60' :
                                       getCardRarityBorder(card.content?.rarity_tier || card.content?.rarity),
                                borderRadius: '8px',
                                overflow: 'hidden',
                                background: '#222',
                                opacity: selectedCardId && !isSelected ? 0.5 : 1,
                                transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                transition: 'all 0.3s ease',
                                boxShadow: isSelected ?
                                    '0 8px 25px rgba(39, 174, 96, 0.4)' :
                                    '0 4px 15px rgba(0,0,0,0.2)'
                            }}
                        >
                            {/* Player Image */}
                            <img
                                src={imgSrc}
                                alt={card.content?.player_name || card.content?.name || 'Card'}
                                onError={e => {
                                    if (card.content_type === 'gear') {
                                        handleGearImageError(e);
                                    } else {
                                        e.target.onerror = null;
                                        e.target.src = '/art/playerImgs/ExamplePlayer.png';
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    filter: 'none'
                                }}
                            />
                            {/* COMPLETE ORIGINAL Card Content Overlay */}
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0,0,0,0.18)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '10px',
                                color: 'white'
                            }}>
                                {/* Player/Item Name - Moved Up for Better Centering */}
                                <div style={{
                                    background: 'rgba(0,0,0,0.8)',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    marginTop: '-5px'
                                }}>
                                    {card.content?.player_name || card.content?.name || 'Unknown'}
                                </div>
                                
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '3px'
                                }}>
                                    {/* Item effect text for Gear Cards - Up to 2 Lines.
                                        Two backend pack paths are live and they name this
                                        field differently: the dynamic legacy-transform
                                        emits `player_description`, while the legacy
                                        GeneratedPack serializer emits only `description`.
                                        Read both so the effect box is never silently blank. */}
                                    {card.content_type === 'gear' &&
                                     (card.content?.player_description || card.content?.description) && (
                                        <div style={{
                                            background: 'rgba(255,255,255,0.9)',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '11px',
                                            textAlign: 'center',
                                            color: '#333',
                                            fontWeight: '600',
                                            maxWidth: '140px',
                                            overflow: 'hidden',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            lineHeight: '1.2',
                                            alignSelf: 'center'
                                        }}>
                                            {card.content.player_description || card.content.description}
                                        </div>
                                    )}

                                    {/* Show rarity for gear cards only */}
                                    {card.content_type === 'gear' && (
                                        <div style={{
                                            background: getCardRarityColor(card.content?.rarity_tier || card.content?.rarity),
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            textAlign: 'center'
                                        }}>
                                            {card.content?.rarity_tier || card.content?.rarity || 'LEGENDARY'}
                                        </div>
                                    )}

                                    {/* NEW: Split Opponent/Projection Boxes for Player Cards */}
                                    {card.content_type === 'player' &&
                                     (card.content?.matchup_display || card.content?.projected_points) && (
                                        <div style={{
                                            display: 'flex',
                                            gap: '4px',
                                            justifyContent: 'center'
                                        }}>
                                            {card.content?.matchup_display && (
                                                <div style={{
                                                    background: 'rgba(0,0,0,0.8)',
                                                    padding: '4px 6px',
                                                    borderRadius: '4px',
                                                    fontSize: '10px',
                                                    textAlign: 'center',
                                                    fontWeight: '600',
                                                    flex: 1
                                                }}>
                                                    <span style={{ fontWeight: 'bold' }}>OPP:</span> {extractOpponentFromMatchup(card.content.matchup_display)}
                                                </div>
                                            )}
                                            {card.content?.projected_points && (
                                                <div style={{
                                                    background: 'rgba(0,0,0,0.8)',
                                                    padding: '4px 6px',
                                                    borderRadius: '4px',
                                                    fontSize: '10px',
                                                    textAlign: 'center',
                                                    fontWeight: '600',
                                                    flex: 1
                                                }}>
                                                    <span style={{ fontWeight: 'bold' }}>PROJ:</span> {Math.round(card.content.projected_points * 10) / 10}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* NEW: Upper Left Position/Team Box - Player Cards Only, Moved Up */}
                            {card.content_type === 'player' && (
                                <div style={{
                                    position: 'absolute',
                                    top: '3px',
                                    left: '6px',
                                    background: 'rgba(255,255,255,0.9)',
                                    padding: '3px 4px',
                                    borderRadius: '3px',
                                    fontSize: '8px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    color: '#333',
                                    lineHeight: '1.1',
                                    minWidth: '18px'
                                }}>
                                    <div>{card.content?.position || card.type}</div>
                                    <div>{extractTeamFromMatchup(card.content?.matchup_display)}</div>
                                </div>
                            )}

                            {/* Info Button - Moved Up */}
                            <button
                                style={{
                                    position: 'absolute',
                                    top: '3px',
                                    right: '6px',
                                    background: 'rgba(255,255,255,0.85)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    color: '#333',
                                    cursor: 'pointer',
                                    zIndex: 2,
                                    transition: 'background-color 0.2s ease'
                                }}
                                onClick={e => {
                                    e.stopPropagation();
                                    handleCardInfo(card);
                                }}
                                onMouseEnter={e => {
                                    e.target.style.backgroundColor = 'rgba(255,255,255,1)';
                                }}
                                onMouseLeave={e => {
                                    e.target.style.backgroundColor = 'rgba(255,255,255,0.85)';
                                }}
                                title="View card details"
                            >i</button>
                            
                            {/* Selection indicator */}
                            {isSelected && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    background: 'rgba(39, 174, 96, 0.9)',
                                    borderRadius: '50%',
                                    width: '50px',
                                    height: '50px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '24px'
                                }}>
                                    ✓
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            
            {selectedCardId && (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: 'rgba(39, 174, 96, 0.1)',
                    borderRadius: '6px',
                    color: '#27ae60',
                    fontWeight: 'bold'
                }}>
                    ✅ Card selected! Processing your selection...
                </div>
            )}
        </div>
        );
    };
    
    // Action buttons removed - no longer needed below pack
    
    // Helper function to get pack opener background
    const getPackOpenerBackground = () => {
        console.log('🔍 PACK_BG_DEBUG: getPackOpenerBackground called');
        console.log('🔍 PACK_BG_DEBUG: draftState?.upcoming_packs =', draftState?.upcoming_packs);
        console.log('🔍 PACK_BG_DEBUG: currentPackConfig =', currentPackConfig);
        
        // Look at the NEXT pack to be opened from upcoming_packs
        if (draftState?.upcoming_packs && draftState.upcoming_packs.length > 0) {
            const nextPack = draftState.upcoming_packs[0];
            console.log('🔍 PACK_BG_DEBUG: Using upcoming_packs[0] =', nextPack);
            
            if (nextPack?.pack_config?.background_image) {
                const result = `url(${nextPack.pack_config.background_image})`;
                console.log('🔍 PACK_BG_DEBUG: Returning from upcoming_packs:', result);
                return result;
            } else {
                console.log('🔍 PACK_BG_DEBUG: upcoming_packs[0] missing background_image');
            }
        } else {
            console.log('🔍 PACK_BG_DEBUG: No upcoming_packs available');
        }
        
        // Fallback: use currentPackConfig if available (for compatibility)
        if (currentPackConfig?.background_image) {
            const result = `url(${currentPackConfig.background_image})`;
            console.log('🔍 PACK_BG_DEBUG: Returning from currentPackConfig:', result);
            return result;
        } else {
            console.log('🔍 PACK_BG_DEBUG: No currentPackConfig.background_image');
        }
        
        // Final fallback
        const fallback = 'url(/art/GoldPackBackground.png)';
        console.log('🔍 PACK_BG_DEBUG: Using fallback:', fallback);
        return fallback;
    };

    // Pack rarity name for the pack-opener overlay, plus the number of packs
    // left in the draft (see getTotalPacksRemaining — it is a whole-draft count,
    // NOT a per-type one, because the backend exposes no per-type breakdown).
    // `remaining` is null only when that count can't be computed, so the caller
    // renders a visibly-degraded '—' instead of a believable fake 0.
    const getPackRarityInfo = () => {
        // The count does not depend on the rarity, so compute it once up front:
        // an unreadable rarity must not also blank out a perfectly good count.
        const remaining = getTotalPacksRemaining();

        if (draftState?.upcoming_packs && draftState.upcoming_packs.length > 0) {
            const nextPack = draftState.upcoming_packs[0];
            const packType = nextPack?.pack_config?.rarity_tier || nextPack?.pack_type;

            if (!packType) {
                console.error(
                    '[SequentialPackSystem] CONTRACT VIOLATION: upcoming_packs[0] has neither ' +
                    'pack_config.rarity_tier nor pack_type — cannot resolve the pack rarity name.',
                    { nextPack, upcomingPacksLength: draftState.upcoming_packs.length }
                );
                return { displayName: 'Pack', remaining };
            }

            let displayName;
            switch (packType) {
                case 'LEGENDARY':
                    displayName = 'Legendary Pack';
                    break;
                case 'RARE':
                    displayName = 'Rare Pack';
                    break;
                // Legacy catalogs use VALUE, dynamic catalogs use COMMON.
                case 'VALUE':
                case 'COMMON':
                    displayName = 'Value Pack';
                    break;
                default:
                    displayName = nextPack?.pack_config?.display_name || 'Pack';
            }

            return { displayName, remaining };
        }

        // No upcoming_packs: the rarity name is unknowable, so it degrades to a
        // generic 'Pack'. The remaining count is independent of upcoming_packs,
        // so it is still reported when it can be computed.
        console.error(
            '[SequentialPackSystem] CONTRACT VIOLATION: draft_state.upcoming_packs is missing or empty ' +
            'while the pack opener is being rendered — cannot resolve the pack rarity name.',
            {
                upcoming_packs: draftState?.upcoming_packs,
                packsCompleted: draftState?.total_packs_completed ?? null,
                isDraftComplete: draftState?.is_draft_complete ?? null
            }
        );
        return { displayName: 'Pack', remaining };
    };

    // Component for drafted players display
    const renderDraftedPlayers = () => {
        // DIAGNOSTIC: Log the entire draftState to see what's available
        console.log('🔍 DIAGNOSTIC - Full draftState:', JSON.stringify(draftState, null, 2));
        console.log('🔍 DIAGNOSTIC - draftState.drafted_players:', draftState?.drafted_players);
        console.log('🔍 DIAGNOSTIC - draftState.draft_progress:', draftState?.draft_progress);
        console.log('🔍 DIAGNOSTIC - draftState keys:', draftState ? Object.keys(draftState) : 'null');
        
        // Try multiple possible locations for drafted players
        const draftedPlayers = draftState?.drafted_players ||
                              draftState?.draft_progress?.drafted_players ||
                              [];
        
        console.log('🔍 DIAGNOSTIC - Final draftedPlayers array:', draftedPlayers);
        
        return (
            <div style={{
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderRadius: '8px',
                border: '2px solid #3498db'
            }}>
                <div style={{
                    textAlign: 'center',
                    marginBottom: '10px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#2c3e50'
                }}>
                    Draft Progress: {getPacksCompleted(draftState) ?? '—'}/{catalogInfo?.total_packs ?? '—'} packs
                </div>
                
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    overflowX: 'auto'
                }}>
                    {draftedPlayers.length === 0 ? (
                        <div style={{
                            padding: '20px',
                            color: '#7f8c8d',
                            fontStyle: 'italic'
                        }}>
                            No players drafted yet
                        </div>
                    ) : (
                        // Sort players by position: QB → RB → WR
                        [...draftedPlayers].sort((a, b) => {
                            const positionOrder = { 'QB': 1, 'RB': 2, 'WR': 3 };
                            const orderA = positionOrder[a.position] || 999;
                            const orderB = positionOrder[b.position] || 999;
                            return orderA - orderB;
                        }).map((player, index) => {
                            const gsisId = player.gsis_id || player.source_player_id;
                            // Prefer backend-resolved presentation URL (equipped variant).
                            // resolveMediaUrl handles the dev cross-port origin mismatch.
                            const imagePath = player.presentation?.image_url
                                ? resolveMediaUrl(player.presentation.image_url)
                                : gsisId
                                    ? `/art/player_images_gsis_cartoon/${gsisId}.png`
                                    : FALLBACK_PLAYER_IMAGE;
                            
                            return (
                                <div
                                    key={player.id || index}
                                    onClick={() => {
                                        setEnlargedItem({ type: 'player', data: player });
                                        setShowEnlargedPopup(true);
                                    }}
                                    style={{
                                        width: '80px',
                                        height: '110px',
                                        position: 'relative',
                                        cursor: 'pointer',
                                        border: '2px solid #3498db',
                                        borderRadius: '6px',
                                        overflow: 'hidden',
                                        background: '#222',
                                        transition: 'transform 0.2s ease',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                >
                                    <img
                                        src={imagePath}
                                        alt={player.name}
                                        onError={e => {
                                            e.target.onerror = null;
                                            e.target.src = '/art/playerImgs/ExamplePlayer.png';
                                        }}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        background: 'rgba(0,0,0,0.8)',
                                        padding: '4px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        color: 'white',
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {player.name}
                                    </div>
                                    <div style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: '4px',
                                        background: 'rgba(255,255,255,0.9)',
                                        padding: '2px 4px',
                                        borderRadius: '3px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        color: '#333'
                                    }}>
                                        {player.position}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        );
    };

    // Component for drafted gear grid
    const renderDraftedGear = () => {
        // DIAGNOSTIC: Check for gear in multiple locations
        console.log('🔍 DIAGNOSTIC GEAR - draftState.drafted_gear:', draftState?.drafted_gear);
        console.log('🔍 DIAGNOSTIC GEAR - draftState.draft_progress?.drafted_gear:', draftState?.draft_progress?.drafted_gear);
        
        const draftedGear = draftState?.drafted_gear ||
                           draftState?.draft_progress?.drafted_gear ||
                           [];
        // Composition via the shared normalizer — same reader as
        // DraftProgressTracker and PackRecommendationEngine. `|| 0` here would
        // turn a broken payload into a believable "0/5"; null renders as '—'.
        const gearComposition = normalizeComposition(draftState, 'SequentialPackSystem.gearCounter');
        const gearCount = getPositionCount(gearComposition, 'gear');
        
        console.log('🔍 DIAGNOSTIC GEAR - Final draftedGear array:', draftedGear);
        
        // CRITICAL: Get max gear from catalogInfo - NO FALLBACKS!
        if (!catalogInfo) {
            throw new Error('❌ MISSING DATA: catalogInfo is required for gear counter display!');
        }
        if (catalogInfo.max_gear_roster === undefined || catalogInfo.max_gear_roster === null) {
            throw new Error('❌ MISSING DATA: catalogInfo.max_gear_roster is missing!');
        }
        
        const maxGear = catalogInfo.max_gear_roster;
        console.log('📊 GEAR COUNTER (NO FALLBACKS):', { gearCount, maxGear });
        
        return (
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <div
                    onClick={() => setShowGearPoolModal(true)}
                    style={{
                        textAlign: 'center',
                        marginBottom: '15px',
                        padding: '8px 16px',
                        backgroundColor: 'rgba(255,255,255,0.92)',
                        borderRadius: '16px',
                        border: '1.5px solid #f39c12',
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        fontSize: '16px',
                        display: 'inline-block',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(243, 156, 18, 0.4)';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}
                >
                    GEAR {gearCount ?? '—'}/{maxGear}
                </div>
                
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '10px',
                    maxWidth: '600px',
                    margin: '0 auto'
                }}>
                    {draftedGear.length === 0 ? (
                        <div style={{
                            gridColumn: '1 / -1',
                            padding: '20px',
                            textAlign: 'center',
                            color: '#ffffff',
                            fontStyle: 'italic'
                        }}>
                            No gear drafted yet
                        </div>
                    ) : (
                        draftedGear.map((gear, index) => (
                            <div
                                key={gear.id || index}
                                onClick={() => {
                                    setEnlargedItem({ type: 'gear', data: gear });
                                    setShowEnlargedPopup(true);
                                }}
                                style={{
                                    width: '100%',
                                    aspectRatio: '3/4',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    border: `2px solid ${getCardRarityColor(gear.rarity_tier || gear.rarity)}`,
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    background: getGearPositionColor(gear),
                                    transition: 'transform 0.2s ease',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <img
                                    src={getGearImageUrl(gear)}
                                    alt={gear.name}
                                    onError={handleGearImageError}
                                    style={{
                                        width: '100%',
                                        height: '70%',
                                        objectFit: 'contain',
                                        padding: '5px'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'rgba(0,0,0,0.85)',
                                    padding: '4px',
                                    fontSize: '9px',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {gear.name}
                                </div>
                                <div style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    background: getCardRarityColor(gear.rarity_tier || gear.rarity),
                                    padding: '2px 4px',
                                    borderRadius: '3px',
                                    fontSize: '8px',
                                    fontWeight: 'bold',
                                    color: 'white'
                                }}>
                                    {gear.rarity_tier || gear.rarity || 'COMMON'}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    // Enlarged item popup component
    const renderEnlargedPopup = () => {
        if (!showEnlargedPopup || !enlargedItem) return null;
        
        const { type, data } = enlargedItem;
        const isPlayer = type === 'player';
        
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 2000,
                    padding: '20px'
                }}
                onClick={() => setShowEnlargedPopup(false)}
            >
                <div
                    style={{
                        position: 'relative',
                        maxWidth: '400px',
                        width: '100%'
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    {isPlayer ? (
                        <div style={{
                            width: '240px',
                            height: '330px',
                            margin: '0 auto',
                            position: 'relative',
                            border: '3px solid #3498db',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: '#222',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
                        }}>
                            <img
                                src={`/art/player_images_gsis_cartoon/${data.gsis_id || data.source_player_id}.png`}
                                alt={data.name}
                                onError={e => {
                                    e.target.onerror = null;
                                    e.target.src = '/art/playerImgs/ExamplePlayer.png';
                                }}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'rgba(0,0,0,0.9)',
                                padding: '12px',
                                color: 'white'
                            }}>
                                <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                                    {data.name}
                                </div>
                                <div style={{ fontSize: '14px', color: '#3498db' }}>
                                    {data.position} • {data.team || 'NFL'}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            width: '300px',
                            height: '420px',
                            margin: '0 auto',
                            position: 'relative',
                            border: `3px solid ${getCardRarityColor(data.rarity_tier || data.rarity)}`,
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: '#f8f9fa',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
                        }}>
                            <img
                                src={getGearImageUrl(data)}
                                alt={data.name}
                                onError={handleGearImageError}
                                style={{
                                    width: '100%',
                                    height: '70%',
                                    objectFit: 'contain',
                                    padding: '20px'
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                background: 'rgba(0,0,0,0.9)',
                                padding: '15px',
                                color: 'white'
                            }}>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                                    {data.name}
                                </div>
                                {(data.player_description || data.description) && (
                                    <div style={{ fontSize: '12px', color: '#ecf0f1', marginBottom: '8px' }}>
                                        {data.player_description || data.description}
                                    </div>
                                )}
                                <div style={{
                                    display: 'inline-block',
                                    background: getCardRarityColor(data.rarity_tier || data.rarity),
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                }}>
                                    {data.rarity_tier || data.rarity || 'COMMON'}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <button
                        onClick={() => setShowEnlargedPopup(false)}
                        style={{
                            display: 'block',
                            margin: '20px auto 0',
                            padding: '12px 40px',
                            backgroundColor: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}
                    >
                        CANCEL
                    </button>
                </div>
            </div>
        );
    };
    
    const handleCloseFirstPackTutorial = () => {
        setShowFirstPackTutorial(false);
    };
    
    const handleMarkFirstPackTutorialViewed = async (pageIdentifier, neverShowAgain) => {
        console.log('First pack tutorial marked as viewed:', pageIdentifier, 'Never show again:', neverShowAgain);
        if (neverShowAgain) {
            localStorage.setItem('tutorials_disabled', 'true');
        }
        localStorage.setItem(`tutorial_viewed_${pageIdentifier}`, 'true');
    };

    const renderPackOpener = () => {
        const packInfo = getPackRarityInfo();
        
        return (
            <div className="pack-to-open" style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '20px'
            }}>
                <div
                    style={{
                        position: 'relative',
                        transform: 'scale(1)',
                        transition: 'transform 0.3s ease',
                        cursor: isOpeningPack ? 'wait' : 'pointer'
                    }}
                    onClick={handleOpenPack}
                    onMouseEnter={(e) => {
                        if (!isOpeningPack) e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                        if (!isOpeningPack) e.currentTarget.style.transform = 'scale(1)';
                    }}
                >
                    <div
                        style={{
                            backgroundImage: getPackOpenerBackground(),
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            width: '180px',
                            height: '320px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: '0 auto',
                            opacity: isOpeningPack ? 0.7 : 1,
                            borderRadius: '8px',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                            transition: 'opacity 0.3s ease'
                        }}
                    >
                    </div>
                    
                    {/* Pack Rarity Overlay - now scales with parent */}
                    <div style={{
                        position: 'absolute',
                        bottom: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(0,0,0,0.85)',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        textAlign: 'center',
                        pointerEvents: 'none',
                        minWidth: '140px'
                    }}>
                        <div style={{
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            marginBottom: '2px'
                        }}>
                            {packInfo.displayName}
                        </div>
                        <div style={{
                            color: '#3498db',
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}>
                            {/* Whole-draft count — "Packs Left", not "N Legendary
                                Remaining". See getTotalPacksRemaining(). */}
                            {packInfo.remaining === null ? '—' : packInfo.remaining} Packs Left
                        </div>
                    </div>
                </div>
            
            </div>
        );
    };

    // Welcome Splash Screen Component
    const renderWelcomeSplash = () => {
        if (!showWelcomeSplash) return null;
        
        // Extract dynamic roster limits from catalogInfo - NO FALLBACKS!
        if (!catalogInfo) {
            throw new Error('❌ MISSING DATA: catalogInfo is null/undefined - cannot display tutorial!');
        }
        
        if (catalogInfo.max_qb_roster === undefined || catalogInfo.max_qb_roster === null) {
            throw new Error('❌ MISSING DATA: catalogInfo.max_qb_roster is missing from API response!');
        }
        if (catalogInfo.max_rb_roster === undefined || catalogInfo.max_rb_roster === null) {
            throw new Error('❌ MISSING DATA: catalogInfo.max_rb_roster is missing from API response!');
        }
        if (catalogInfo.max_wr_roster === undefined || catalogInfo.max_wr_roster === null) {
            throw new Error('❌ MISSING DATA: catalogInfo.max_wr_roster is missing from API response!');
        }
        if (catalogInfo.max_gear_roster === undefined || catalogInfo.max_gear_roster === null) {
            throw new Error('❌ MISSING DATA: catalogInfo.max_gear_roster is missing from API response!');
        }
        
        const maxQB = catalogInfo.max_qb_roster;
        const maxRB = catalogInfo.max_rb_roster;
        const maxWR = catalogInfo.max_wr_roster;
        const maxGear = catalogInfo.max_gear_roster;
        
        console.log('📊 TUTORIAL ROSTER LIMITS (NO FALLBACKS):', {
            maxQB,
            maxRB,
            maxWR,
            maxGear,
            source: 'API catalog_info (LeagueTemplate roster limits)'
        });
        
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    zIndex: 9999,
                    padding: isMobile ? '10px' : '20px',
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch'
                }}
                onClick={() => setShowWelcomeSplash(false)}
            >
                <div
                    style={{
                        backgroundColor: '#2c3e50',
                        border: '3px solid #3498db',
                        borderRadius: '12px',
                        padding: isMobile ? '20px 15px' : '50px 40px',
                        maxWidth: '700px',
                        width: '100%',
                        maxHeight: isMobile ? 'none' : '90vh',
                        overflowY: isMobile ? 'visible' : 'auto',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                        textAlign: 'center',
                        color: 'white',
                        margin: isMobile ? '10px auto' : 'auto',
                        position: 'relative'
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <h1 style={{
                        fontSize: isMobile ? '24px' : '32px',
                        marginBottom: '20px',
                        color: '#3498db',
                        fontWeight: 'bold'
                    }}>
                        Prepare yourself for the most epic of drafts!
                    </h1>
                    
                    <p style={{
                        fontSize: isMobile ? '16px' : '18px',
                        marginBottom: '30px',
                        lineHeight: '1.6'
                    }}>
                        Pick the right combination of superheroes and gear<br/>
                        to dominate the battlefield.
                    </p>
                    
                    <div style={{
                        textAlign: 'left',
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        padding: '20px',
                        borderRadius: '8px',
                        marginBottom: '30px',
                        fontSize: isMobile ? '14px' : '16px',
                        lineHeight: '1.8'
                    }}>
                        <p style={{ marginBottom: '10px' }}>
                            At the top shows how many of each position you need to pick.
                        </p>
                        
                        {/* Example Position Bubbles */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '20px',
                            gap: '8px',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{
                                background: 'rgba(255,255,255,0.92)',
                                padding: '4px 10px',
                                borderRadius: '16px',
                                border: '1.5px solid #3498db',
                                fontWeight: 'bold',
                                color: '#2c3e50',
                                fontSize: '13px',
                                minWidth: '55px',
                                textAlign: 'center'
                            }}>
                               QB 0/{maxQB}
                           </div>
                           <div style={{
                               background: 'rgba(255,255,255,0.92)',
                               padding: '4px 10px',
                               borderRadius: '16px',
                               border: '1.5px solid #e74c3c',
                               fontWeight: 'bold',
                               color: '#2c3e50',
                               fontSize: '13px',
                               minWidth: '55px',
                               textAlign: 'center'
                           }}>
                               RB 0/{maxRB}
                           </div>
                           <div style={{
                               background: 'rgba(255,255,255,0.92)',
                               padding: '4px 10px',
                               borderRadius: '16px',
                               border: '1.5px solid #27ae60',
                               fontWeight: 'bold',
                               color: '#2c3e50',
                               fontSize: '13px',
                               minWidth: '55px',
                               textAlign: 'center'
                           }}>
                               WR 0/{maxWR}
                            </div>
                        </div>
                        
                        <p style={{ marginBottom: '10px' }}>
                            At the bottom it shows how many items of gear you need to pick.
                        </p>
                        
                        {/* Example Gear Bubble */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '20px'
                        }}>
                            <div style={{
                                background: 'rgba(255,255,255,0.92)',
                                padding: '8px 16px',
                                borderRadius: '16px',
                                border: '1.5px solid #f39c12',
                                fontWeight: 'bold',
                                color: '#2c3e50',
                                fontSize: '16px',
                                display: 'inline-block'
                            }}>
                                GEAR 0/{maxGear}
                            </div>
                        </div>
                        
                        <p style={{ marginBottom: '15px' }}>
                            Click any of those buttons to see the pool of available options.<br/>Keep in mind:<br/>Players can only equip THREE pieces of gear<br/>
Gear is color coordinated to show what you’ve drafted for what position so far
                        </p>
                        
                        <div style={{ paddingLeft: '20px' }}>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#f1c40f', fontWeight: 'bold' }}>QB: Yellow</span>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#3498db', fontWeight: 'bold' }}>RB: Blue</span>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>WR: Red</span>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#27ae60', fontWeight: 'bold' }}>QB/RB: Green</span>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#9b59b6', fontWeight: 'bold' }}>RB/WR: Purple</span>
                            </div>
                            <div>
                                <span style={{ color: '#ffffff', fontWeight: 'bold' }}>Not Position Specific: White</span>
                            </div>
                        </div>
                    </div>
                    
                    <button
                        onClick={() => setShowWelcomeSplash(false)}
                        style={{
                            padding: '15px 40px',
                            backgroundColor: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: isMobile ? '16px' : '18px',
                            boxShadow: '0 4px 12px rgba(52, 152, 219, 0.4)',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#2980b9';
                            e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#3498db';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        LET'S DRAFT!
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="sequential-pack-system enhanced" style={{
            minHeight: '100vh',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            paddingTop: '100px'
        }}>
            {/* Welcome Splash Screen */}
            {renderWelcomeSplash()}
            
            {/* Navigation Bar
                Horizontal padding lives in SequentialPackSystem.css (.draft-nav-bar) so the
                right side can reserve room for the fixed AccountButton at every breakpoint —
                inline padding here would win over the media queries and re-introduce the
                AUTODRAFT / user-icon overlap. */}
            <div className="draft-nav-bar" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '90px',
                backgroundColor: 'rgba(0,0,0,0.85)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 100,
                boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
            }}>
                {/* LEAGUES button (left) */}
                <button
                    className="draft-nav-button"
                    onClick={() => setPage('HOME')}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        minWidth: '100px'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                    }}
                >
                    LEAGUES
                </button>
                
                {/* Logo (center) - bigger and lower */}
                <img
                    className="draft-nav-logo"
                    src="/art/Logo.png"
                    alt="Mythic Football"
                    style={{
                        height: '70px',
                        objectFit: 'contain',
                        marginTop: '10px'
                    }}
                />
                
                {/* AUTODRAFT button (right) - same style as LEAGUES */}
                <button
                    className="draft-nav-button"
                    onClick={handleAutodraft}
                    disabled={!draftState || draftState.is_draft_complete || isLoading}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderRadius: '6px',
                        cursor: !draftState || draftState.is_draft_complete || isLoading ? 'not-allowed' : 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        transition: 'all 0.2s ease',
                        minWidth: '100px',
                        opacity: !draftState || draftState.is_draft_complete || isLoading ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                        if (!(!draftState || draftState.is_draft_complete || isLoading)) {
                            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!(!draftState || draftState.is_draft_complete || isLoading)) {
                            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                        }
                    }}
                >
                    AUTODRAFT
                </button>
            </div>

            {/* Drafted Players Display */}
            {draftState && renderDraftedPlayers()}

            {/* Position Counters (QB/RB/WR only, GEAR moved below) - NO FALLBACKS! */}
            {draftState && (() => {
                // CRITICAL: Extract roster limits from catalogInfo - NO FALLBACKS!
                if (!catalogInfo) {
                    throw new Error('❌ MISSING DATA: catalogInfo is required for position counter display!');
                }
                
                if (catalogInfo.max_qb_roster === undefined || catalogInfo.max_qb_roster === null) {
                    throw new Error('❌ MISSING DATA: catalogInfo.max_qb_roster is missing!');
                }
                if (catalogInfo.max_rb_roster === undefined || catalogInfo.max_rb_roster === null) {
                    throw new Error('❌ MISSING DATA: catalogInfo.max_rb_roster is missing!');
                }
                if (catalogInfo.max_wr_roster === undefined || catalogInfo.max_wr_roster === null) {
                    throw new Error('❌ MISSING DATA: catalogInfo.max_wr_roster is missing!');
                }
                
                const maxQB = catalogInfo.max_qb_roster;
                const maxRB = catalogInfo.max_rb_roster;
                const maxWR = catalogInfo.max_wr_roster;
                
                // Counts via the shared normalizer — a missing count renders as
                // '—' (and is logged) instead of a plausible-looking 0.
                const counterComposition = normalizeComposition(draftState, 'SequentialPackSystem.positionCounters');
                const qbCount = getPositionCount(counterComposition, 'qb');
                const rbCount = getPositionCount(counterComposition, 'rb');
                const wrCount = getPositionCount(counterComposition, 'wr');
                
                console.log('📊 POSITION COUNTERS (NO FALLBACKS):', {
                    maxQB, maxRB, maxWR, qbCount, rbCount, wrCount
                });
                
                return (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: '20px',
                        gap: '8px',
                        flexWrap: 'wrap'
                    }}>
                        <div
                            onClick={() => {
                                setSelectedPosition('QB');
                                setShowPlayerPoolModal(true);
                            }}
                            style={{
                                background: 'rgba(255,255,255,0.92)',
                                padding: '4px 10px',
                                borderRadius: '16px',
                                border: '1.5px solid #3498db',
                                fontWeight: 'bold',
                                color: '#2c3e50',
                                fontSize: '13px',
                                minWidth: '55px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(52, 152, 219, 0.4)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            QB {qbCount ?? '—'}/{maxQB}
                        </div>
                        <div
                            onClick={() => {
                                setSelectedPosition('RB');
                                setShowPlayerPoolModal(true);
                            }}
                            style={{
                                background: 'rgba(255,255,255,0.92)',
                                padding: '4px 10px',
                                borderRadius: '16px',
                                border: '1.5px solid #e74c3c',
                                fontWeight: 'bold',
                                color: '#2c3e50',
                                fontSize: '13px',
                                minWidth: '55px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(231, 76, 60, 0.4)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            RB {rbCount ?? '—'}/{maxRB}
                        </div>
                        <div
                            onClick={() => {
                                setSelectedPosition('WR');
                                setShowPlayerPoolModal(true);
                            }}
                            style={{
                                background: 'rgba(255,255,255,0.92)',
                                padding: '4px 10px',
                                borderRadius: '16px',
                                border: '1.5px solid #27ae60',
                                fontWeight: 'bold',
                                color: '#2c3e50',
                                fontSize: '13px',
                                minWidth: '55px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(39, 174, 96, 0.4)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            WR {wrCount ?? '—'}/{maxWR}
                        </div>
                        {draftState.is_draft_complete && (
                            <div style={{
                                background: 'rgba(39, 174, 96, 0.92)',
                                padding: '4px 10px',
                                borderRadius: '16px',
                                border: '1.5px solid #27ae60',
                                fontWeight: 'bold',
                                color: 'white',
                                fontSize: '13px',
                                minWidth: '80px',
                                textAlign: 'center'
                            }}>
                                ✅ COMPLETE
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Main Draft Interface */}
            <div className="draft-main-content">
                {renderDraftInterface()}
            </div>

            {/* Drafted Gear Grid (below pack) */}
            {draftState && renderDraftedGear()}

            {/* Card Details Modal */}
            <CardInfoPopup
                card={modalCard}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                isMobile={isMobile}
            />

            {/* Enlarged Item Popup */}
            {renderEnlargedPopup()}

            {/* Player Pool Modal */}
            <PlayerPoolModal
                isOpen={showPlayerPoolModal}
                onClose={() => setShowPlayerPoolModal(false)}
                teamId={effectiveTeamId}
                position={selectedPosition}
            />

            {/* Gear Pool Modal */}
            <GearPoolModal
                isOpen={showGearPoolModal}
                onClose={() => setShowGearPoolModal(false)}
                teamId={effectiveTeamId}
            />
            
            {/* First Pack Tutorial */}
            {showFirstPackTutorial && (
                <TutorialPopup
                    tutorial={firstPackTutorialData}
                    onClose={handleCloseFirstPackTutorial}
                    onMarkViewed={handleMarkFirstPackTutorialViewed}
                />
            )}

            {/* Instructions for pack selection */}
            <div className="draft-instructions" style={{ marginTop: '20px' }}>
                <p style={{ color: 'white', textAlign: 'center' }}>
                    You only get {catalogInfo?.total_packs} packs. Choose wisely!
                </p>
            </div>
        </div>
    );
}

export default SequentialPackSystem;
